import { captureException } from "@sentry/react";
import { logButtonClickAtom } from "@sunrise/analytics";
import { actionJWTClear, jwtAtom } from "@sunrise/jwt";
import { useTranslator } from "@sunrise/translator";
import { useAtomCallback } from "jotai/utils";
import { ReactElement, Suspense, useCallback } from "react";

import { FocusButton, Spinner } from "@/components";
import { globalFocusKey } from "@/config/focus-key";
import { useMenu } from "@/features/menu/use-menu";
import { useRoutes } from "@/features/routing/use-routes";
import { SettingsBase } from "@/features/settings/settings-base";
import { closeApp } from "@/modules/tizen/system.service";
import * as settingsStyle from "@/routes/settings.css";
import { FocusContainer } from "@/utils/focus-container";

import { ProfileSubscription } from "./profile-subscription";
import { buttons, root } from "./profile.css";
import { LoggedOutError } from "@sunrise/backend-core";

export function Profile(): ReactElement {
  useMenu({ hidden: true });
  const routes = useRoutes();
  const t = useTranslator();

  const doLogOut = useAtomCallback(
    useCallback(async (get, set) => {
      const log = get(logButtonClickAtom);

      await log.invoke({
        type: "do_logout",
      });

      captureException(new LoggedOutError("user_requested_logout"));
      set(jwtAtom, actionJWTClear());
    }, []),
  );

  return (
    <SettingsBase
      titleLabel="menu_profile"
      data-testid="profile"
      className={root}
    >
      <Suspense fallback={<Spinner data-testid="profile-spinner" />}>
        <ProfileSubscription />
      </Suspense>

      <FocusContainer
        focusKey={globalFocusKey.activePage}
        onBack={routes.back}
        shouldFocus
      >
        {() => (
          <div className={buttons}>
            <FocusButton
              className={settingsStyle.settingsButton}
              text={t("button_logout")}
              onEnterPress={doLogOut}
              variant="ghost"
              data-testid="profile.button.logout"
            />
            <FocusButton
              className={settingsStyle.settingsButton}
              text={t("button_close_app")}
              onEnterPress={closeApp}
              variant="ghost"
              data-testid="profile.button.close"
            />
          </div>
        )}
      </FocusContainer>
    </SettingsBase>
  );
}
